import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Props = {
  secondsUntilExpiry: number;
  onUpgrade: () => void;
};

function formatCountdown(seconds: number) {
  const hours = Math.max(0, Math.ceil(seconds / 3600));
  return `${hours}h`;
}

export function SubscriptionExpiryBanner({ secondsUntilExpiry, onUpgrade }: Props) {
  const { t } = useTranslation();
  const [liveSeconds, setLiveSeconds] = useState(secondsUntilExpiry);

  useEffect(() => {
    setLiveSeconds(secondsUntilExpiry);
  }, [secondsUntilExpiry]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setLiveSeconds((current) => Math.max(0, current - 60));
    }, 60_000);

    return () => window.clearInterval(timer);
  }, [liveSeconds]);

  const isFinalWindow = liveSeconds <= 24 * 60 * 60;

  const copy = useMemo(() => {
    return t("subscription.banner.copy", { time: formatCountdown(liveSeconds) });
  }, [liveSeconds, t]);

  return (
    <div
      className={cn(
        "mb-4 flex flex-col gap-3 rounded-xl border px-4 py-3 md:flex-row md:items-center md:justify-between",
        isFinalWindow
          ? "border-amber-300 bg-amber-50 text-amber-900"
          : "border-sky-300 bg-sky-50 text-sky-900",
      )}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
        <div>
          <p className="text-sm font-semibold">{t("subscription.banner.title")}</p>
          <p className="text-sm">{copy}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 self-end md:self-auto">
        <Button onClick={onUpgrade} className="bg-accent text-accent-foreground hover:bg-accent/90">
          {t("subscription.upgrade")}
        </Button>
      </div>
    </div>
  );
}
